import { ReactNode, useState } from 'react';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { colors } from '../../theme/tokens';

// XD per-row actions ("Common structure" in DESIGN.md): "•••" at
// rest that on mouse-over expands into a strip of icons. Used by
// the documents table (view, edit, download, delete) and the users
// list (edit, delete); each screen decides which actions it offers.
//
// The "•••" is also a button that opens a menu with the same actions: it is
// what the keyboard and touch screens use (tablet and cards on mobile),
// where there is no hover. In the table the two groups overlap in the same
// cell and the row (`tr:hover`) decides which one is seen — see ListTable, which
// sets the `.RowActions-icons` / `.RowActions-rest` rules.
//
// `expand` is the document-card behaviour of the "Flux 2" grid (DESIGN.md,
// "Revisión desktop"): tapping the "•••" swaps it in place for the strip of
// icons, with no hover and no menu.

export type RowAction<K extends string> = {
  key: K;
  label: string;
  icon: ReactNode;
};

type Props<K extends string> = {
  /** Name of the row, for the accessible labels ("Actions pour …"). */
  subject: string;
  actions: RowAction<K>[];
  /**
   * `table` adds the icon strip for hover; `card` only the menu; `expand`
   * swaps the "•••" for the strip in place when tapped (the grid cards).
   */
  variant: 'table' | 'card' | 'expand';
  /**
   * Width of the strip in the table. By default the one the given actions
   * require; a listing whose rows offer a different number of actions passes
   * that of the longest row so that they all stay aligned.
   */
  width?: number;
  onAction: (action: K) => void;
};

// XD: the "•••" and the expanded action icons are orange at rest.
const ICON_BUTTON_SX = {
  color: colors.accent,
  p: 0.75,
  '&:hover': { color: colors.accentLight },
} as const;

// Width of each icon in the strip (36 px button plus the gap).
const ICON_SLOT = 38;

// Width of the strip for `n` actions, so that the table reserves the same
// space in the header and the row does not move when going from "•••" to icons.
export const rowActionsWidth = (count: number): number =>
  count * ICON_SLOT + 16;

const RowActions = <K extends string>({
  subject,
  actions,
  variant,
  width,
  onAction,
}: Props<K>) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);

  const run = (action: K) => {
    setAnchor(null);
    onAction(action);
  };

  if (variant === 'expand') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 36,
          // The strip replaces the "•••" in place, left-aligned as in the
          // artboard card.
          justifyContent: 'flex-start',
        }}
      >
        {expanded ? (
          actions.map((action) => (
            <Tooltip key={action.key} title={action.label}>
              <IconButton
                aria-label={`${action.label} ${subject}`}
                onClick={() => run(action.key)}
                sx={{ ...ICON_BUTTON_SX, p: 0.5 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))
        ) : (
          <IconButton
            aria-label={`Actions pour ${subject}`}
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            sx={{ ...ICON_BUTTON_SX, p: 0.5 }}
          >
            <MoreHorizIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  const menu = (
    <>
      <IconButton
        className="RowActions-menu"
        aria-label={`Actions pour ${subject}`}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={ICON_BUTTON_SX}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actions.map((action) => (
          <MenuItem key={action.key} onClick={() => run(action.key)}>
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  if (variant === 'card') {
    return menu;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        // Fixed width so that the "•••" ↔ icons switch does not move the row.
        width: width ?? rowActionsWidth(actions.length),
        height: 40,
        marginLeft: 'auto',
      }}
    >
      <Box
        className="RowActions-rest"
        sx={{ position: 'absolute', right: 0, display: 'flex' }}
      >
        {menu}
      </Box>
      <Box
        className="RowActions-icons"
        sx={{
          position: 'absolute',
          right: 0,
          display: 'flex',
          gap: 0.25,
          // `visibility` and not `opacity`: an invisible button must not
          // receive keyboard focus.
          visibility: 'hidden',
        }}
      >
        {actions.map((action) => (
          <Tooltip key={action.key} title={action.label}>
            <IconButton
              aria-label={`${action.label} ${subject}`}
              onClick={() => run(action.key)}
              sx={ICON_BUTTON_SX}
            >
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default RowActions;
